import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES, type Role } from '@thottbot/shared';
import { apiGet, apiSend, useMe } from '../../api/client';
import { relativeTime } from '../../util/time';
import { describeError } from '../Submit';
import { ConfirmButton } from '../ModQueue';

interface UserRow {
  id: string;
  battletag: string;
  role: Role;
  banned: boolean;
  banReason: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  submissions: number;
  comments: number;
  screenshots: number;
  /** Null means the site-wide daily default applies. */
  buildAllowance: number | null;
}

export function Users() {
  const me = useMe();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const list = useQuery({
    queryKey: ['admin', 'users', term],
    queryFn: () =>
      apiGet<{ items: UserRow[]; total: number }>(`/api/admin/users?q=${encodeURIComponent(term)}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      refresh();
    } catch (err) {
      setError(describeError(err));
    }
  };
  return (
    <div data-testid="admin-users">
      <form
        className="filters"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          setTerm(q.trim());
        }}
      >
        <input
          type="search"
          aria-label="Search users"
          placeholder="BattleTag"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit">Search</button>
        <label>
          Ban reason{' '}
          <input
            type="text"
            aria-label="Ban reason"
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
            maxLength={500}
            placeholder="required to ban"
          />
        </label>
      </form>
      {error && <div className="error">{error}</div>}
      {list.isPending && <p className="muted">Loading...</p>}
      {list.data && (
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr>
                <th>BattleTag</th>
                <th>Role</th>
                <th>Status</th>
                <th>Submissions</th>
                <th>Comments</th>
                <th>Screenshots</th>
                <th>Build allowance</th>
                <th>Last login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((u) => {
                const self = u.id === me.data?.user?.id;
                return (
                  <tr key={u.id} data-testid="user-row">
                    <td>{u.battletag}</td>
                    <td>
                      <select
                        aria-label={`Role for ${u.battletag}`}
                        value={u.role}
                        disabled={self}
                        onChange={(e) =>
                          act(() =>
                            apiSend('PATCH', `/api/admin/users/${u.id}`, { role: e.target.value }),
                          )
                        }
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {u.banned ? (
                        <span className="error">
                          Banned{u.banReason ? ` (${u.banReason})` : ''}
                        </span>
                      ) : (
                        'Active'
                      )}
                    </td>
                    <td>{u.submissions}</td>
                    <td>{u.comments}</td>
                    <td>{u.screenshots}</td>
                    <td>
                      <AllowanceCell
                        user={u}
                        onSave={(value) => act(() => apiSend('PATCH', `/api/admin/users/${u.id}`, { buildAllowance: value }))}
                      />
                    </td>
                    <td>{u.lastLoginAt ? relativeTime(u.lastLoginAt) : 'never'}</td>
                    <td>
                      {self ? (
                        <span className="muted">you</span>
                      ) : u.banned ? (
                        <button
                          type="button"
                          onClick={() =>
                            act(() => apiSend('POST', `/api/admin/users/${u.id}/unban`))
                          }
                        >
                          Unban
                        </button>
                      ) : (
                        <ConfirmButton
                          label="Ban"
                          onConfirm={() => {
                            if (!banReason.trim()) {
                              setError('Enter a ban reason first.');
                              return;
                            }
                            return act(() =>
                              apiSend('POST', `/api/admin/users/${u.id}/ban`, {
                                reason: banReason.trim(),
                              }),
                            );
                          }}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Per-account daily build allowance. Empty means "use the site default", which is a real
 * value here rather than zero: clearing the box restores the default, it does not ban builds.
 */
function AllowanceCell({ user, onSave }: { user: UserRow; onSave: (value: number | null) => void }) {
  const [value, setValue] = useState(user.buildAllowance === null ? '' : String(user.buildAllowance));
  const dirty = value !== (user.buildAllowance === null ? '' : String(user.buildAllowance));
  return (
    <span className="allowance">
      <input
        type="number"
        min={0}
        max={100}
        value={value}
        aria-label={`Build allowance for ${user.battletag}`}
        placeholder="default"
        style={{ width: '5em' }}
        onChange={(e) => setValue(e.target.value)}
      />{' '}
      <button
        type="button"
        disabled={!dirty}
        aria-label={`Save build allowance for ${user.battletag}`}
        onClick={() => onSave(value.trim() === '' ? null : Number(value))}
      >
        Save
      </button>
      <div className="muted">{user.buildAllowance === null ? 'default' : `${user.buildAllowance}/day`}</div>
    </span>
  );
}
